import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight, Plus, CaretDown, Trash, CheckCircle,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";

const AGENT_KEY = "supplier-ageing";
const CHAT_ENDPOINT = "/erp-agents/supplier-ageing/chat";

/* ── Markdown renderer for assistant messages ─────────────────────────────── */
function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-[#0020C2]/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...props} />
          ),
          a: ({ node, ...props }) => <a className="text-[#0020C2] underline" {...props} />,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/* ── Ageing dashboard widget (supplier) ───────────────────────────────────────
   Consumes the ageing_dashboard JSON:
     widget.currencies[ccy] -> { total_liability, overdue_ratio, risk_score,
       risk_level, buckets[], top_suppliers[], insights[], recommendations[] }
   Drill-down hits /erp-agents/supplier-ageing/details with the scope
   (supplier OR business_unit) taken from widget.scope_type/scope_value.        */
function AgeingDashboard({ widget }) {
  const BUCKET_COLORS = {
    "Current": "#10B981",
    "1-30":    "#3B82F6",
    "31-60":   "#F59E0B",
    "61-90":   "#F97316",
    "90+":     "#DC2626",
  };
  const RISK_COLORS = {
    LOW: "#10B981", MEDIUM: "#F59E0B", HIGH: "#F97316", CRITICAL: "#DC2626",
  };

  const scopeType = widget?.scope_type;               // "supplier" | "business_unit"
  const scopeValue = widget?.scope_value;

  // Which of the 5 dashboard buckets to SHOW for the selected period pill.
  const ALL_BUCKETS = ["Current", "1-30", "31-60", "61-90", "90+"];
  const periodDays = widget?.window_days || 0;
  const _bucketCap = {
    30:  ["Current", "1-30"],
    90:  ["Current", "1-30", "31-60", "61-90"],
    120: ["Current", "1-30", "31-60", "61-90", "90+"],
    360: ["Current", "1-30", "31-60", "61-90", "90+"],
  };
  const visibleBuckets = periodDays && _bucketCap[periodDays]
    ? _bucketCap[periodDays]
    : ALL_BUCKETS;
  const isBucketVisible = (label) => visibleBuckets.includes(label);

  // NOTE: all hooks MUST be declared before any early return (rules-of-hooks).
  const [drill, setDrill] = React.useState(null); // {key, title, currency, bucket, supplier}
  const [drillRows, setDrillRows] = React.useState([]);
  const [drillLoading, setDrillLoading] = React.useState(false);
  const [drillErr, setDrillErr] = React.useState("");
  const [showAllSuppliers, setShowAllSuppliers] = React.useState({}); // {currency: bool}

  // Base params for the scope — supplier scope sends `supplier`, BU scope sends
  // `business_unit`. A tapped supplier row inside a BU scope narrows further via
  // `supplier_filter`.
  const scopeParams = React.useCallback(() => {
    if (scopeType === "supplier") return { supplier: scopeValue };
    return { business_unit: scopeValue };
  }, [scopeType, scopeValue]);

  const openDrill = async ({ key, title, currency, bucket, supplier }) => {
    // Toggle closed if the same target is tapped again.
    if (drill && drill.key === key) {
      setDrill(null); setDrillRows([]); setDrillErr("");
      return;
    }
    setDrill({ key, title, currency, bucket, supplier });
    setDrillRows([]); setDrillErr(""); setDrillLoading(true);
    try {
      const params = {
        ...scopeParams(),
        ...(bucket ? { bucket } : {}),
        ...(currency ? { currency } : {}),
      };
      // In a BU scope, a tapped supplier narrows via supplier_filter; in a
      // supplier scope the base `supplier` already scopes it.
      if (supplier && scopeType === "business_unit") params.supplier_filter = supplier;
      const { data } = await api.get("/erp-agents/supplier-ageing/details", { params });
      if (data?.error) setDrillErr("Couldn't load invoices.");
      setDrillRows(data?.items || []);
    } catch (e) {
      setDrillErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setDrillLoading(false);
    }
  };

  const DrillPanel = ({ forKey }) => {
    if (!drill || drill.key !== forKey) return null;
    return (
      <div className="mt-1 mb-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-[#0020C2]/[0.02]"
        data-testid="supplier-drilldown">
        <div className="px-3 py-1.5 bg-[#0020C2]/5 text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center justify-between">
          <span>{drill.title}</span>
          {!drillLoading && <span className="font-normal normal-case text-gray-500">{drillRows.length} invoice(s)</span>}
        </div>
        {drillLoading && (
          <div className="px-3 py-3 text-xs text-gray-400 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading invoices…
          </div>
        )}
        {drillErr && <div className="px-3 py-2 text-xs text-red-600">{drillErr}</div>}
        {!drillLoading && !drillErr && drillRows.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No invoices found.</div>
        )}
        {!drillLoading && !drillErr && drillRows.length > 0 && (
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-[11px] border-collapse">
              <thead className="bg-white sticky top-0">
                <tr>
                  <th className="border-b border-gray-200 px-2 py-1 text-left font-bold uppercase tracking-wider text-[9px] text-gray-500">Invoice</th>
                  <th className="border-b border-gray-200 px-2 py-1 text-left font-bold uppercase tracking-wider text-[9px] text-gray-500">Supplier</th>
                  <th className="border-b border-gray-200 px-2 py-1 text-left font-bold uppercase tracking-wider text-[9px] text-gray-500">Due</th>
                  <th className="border-b border-gray-200 px-2 py-1 text-right font-bold uppercase tracking-wider text-[9px] text-gray-500">Days</th>
                  <th className="border-b border-gray-200 px-2 py-1 text-right font-bold uppercase tracking-wider text-[9px] text-gray-500">Amount</th>
                  <th className="border-b border-gray-200 px-2 py-1 text-left font-bold uppercase tracking-wider text-[9px] text-gray-500">Why</th>
                </tr>
              </thead>
              <tbody>
                {drillRows.map((r, i) => (
                  <tr key={`${r.invoice_number}-${i}`} className="hover:bg-white">
                    <td className="border-b border-gray-100 px-2 py-1 font-mono">{r.invoice_number || "—"}</td>
                    <td className="border-b border-gray-100 px-2 py-1 truncate max-w-[140px]">{r.supplier}</td>
                    <td className="border-b border-gray-100 px-2 py-1">{r.due_date || "—"}</td>
                    <td className="border-b border-gray-100 px-2 py-1 text-right">{r.days_overdue}</td>
                    <td className="border-b border-gray-100 px-2 py-1 text-right font-mono">{r.currency} {Number(r.outstanding_amount).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                    <td className="border-b border-gray-100 px-2 py-1">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${r.is_on_hold ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-600"}`}>
                        {r.why}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Safe to bail now — all hooks above have already been called this render.
  if (!widget || !widget.currencies) return null;

  const currencies = Object.entries(widget.currencies);

  return (
    <div className="mt-2 space-y-3" data-testid="supplier-ageing-dashboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2] flex items-center gap-1">
          <Sparkle size={10} weight="fill" />
          Supplier ageing — {scopeType === "supplier" ? "Supplier" : "Business Unit"}: {scopeValue}
          {widget.window && (
            <span className="ml-1 text-[9px] font-normal text-gray-500 normal-case">
              · {widget.window}
            </span>
          )}
        </div>
      </div>

      {currencies.map(([currency, c]) => {
        const total = c.total_liability || 0;
        return (
          <div key={currency} className="border border-gray-200 rounded-xl bg-white p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-black text-gray-900">{currency}</div>
              <div className="flex items-center gap-3 text-[11px]">
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Owed</div>
                  <div className="font-bold text-gray-900">{currency} {total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Overdue</div>
                  <div className="font-bold text-gray-900">{c.overdue_ratio}%</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-400 uppercase tracking-wider text-[9px] font-bold">Risk</div>
                  <div className="font-bold" style={{ color: RISK_COLORS[c.risk_level] || "#374151" }}>
                    {c.risk_score}/100 · {c.risk_level}
                  </div>
                </div>
              </div>
            </div>

            {total > 0 && (
              <div className="space-y-1">
                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                  {c.buckets.filter((b) => isBucketVisible(b.bucket)).map((b) => {
                    const pct = total > 0 ? (b.amount / total) * 100 : 0;
                    if (pct <= 0) return null;
                    return (
                      <div
                        key={b.bucket}
                        style={{ width: `${pct}%`, background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }}
                        title={`${b.bucket}: ${currency} ${b.amount.toLocaleString()} (${b.percentage}%)`}
                      />
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {c.buckets.filter((b) => isBucketVisible(b.bucket)).map((b) => (
                    <span key={b.bucket} className="inline-flex items-center gap-1 text-[9px] text-gray-600">
                      <span className="w-2 h-2 rounded-sm" style={{ background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }} />
                      {b.bucket}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#0020C2]/5">
                  <tr>
                    <th className="border border-gray-200 px-2 py-1 text-left text-[10px] font-bold uppercase tracking-wider text-gray-600">Bucket</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Amount</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Inv</th>
                    <th className="border border-gray-200 px-2 py-1 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">%</th>
                  </tr>
                </thead>
                <tbody>
                  {c.buckets.filter((b) => isBucketVisible(b.bucket)).map((b) => {
                    const dkey = `bucket:${currency}:${b.bucket}`;
                    const clickable = b.invoice_count > 0;
                    return (
                    <React.Fragment key={b.bucket}>
                    <tr>
                      <td className="border border-gray-200 px-2 py-1">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-sm" style={{ background: BUCKET_COLORS[b.bucket] || "#9CA3AF" }} />
                          {b.bucket}
                        </span>
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right font-mono">{currency} {b.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      <td className="border border-gray-200 px-2 py-1 text-right">
                        {clickable ? (
                          <button
                            onClick={() => openDrill({
                              key: dkey,
                              title: `${b.bucket} · ${currency}`,
                              currency, bucket: b.bucket, supplier: null,
                            })}
                            className={`font-bold underline decoration-dotted underline-offset-2
                              ${drill && drill.key === dkey ? "text-[#0020C2]" : "text-[#0020C2]/80 hover:text-[#0020C2]"}`}
                            title="Show these invoices">
                            {b.invoice_count}
                          </button>
                        ) : b.invoice_count}
                      </td>
                      <td className="border border-gray-200 px-2 py-1 text-right">{b.percentage}%</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="p-0 border-0">
                        <DrillPanel forKey={dkey} />
                      </td>
                    </tr>
                    </React.Fragment>
                  );})}
                  {(() => {
                    const vis = c.buckets.filter((b) => isBucketVisible(b.bucket));
                    const vAmt = vis.reduce((s, b) => s + (b.amount || 0), 0);
                    const vCnt = vis.reduce((s, b) => s + (b.invoice_count || 0), 0);
                    const vPct = vis.reduce((s, b) => s + (b.percentage || 0), 0);
                    return (
                      <tr className="font-bold bg-gray-50">
                        <td className="border border-gray-200 px-2 py-1">Total</td>
                        <td className="border border-gray-200 px-2 py-1 text-right font-mono">{currency} {vAmt.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">{vCnt}</td>
                        <td className="border border-gray-200 px-2 py-1 text-right">{Math.round(vPct)}%</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>

            {c.top_suppliers && c.top_suppliers.length > 0 && scopeType === "business_unit" && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    Suppliers ({c.top_suppliers.length})
                  </div>
                  {c.top_suppliers.length > 5 && (
                    <button
                      onClick={() => setShowAllSuppliers((prev) => ({ ...prev, [currency]: !prev[currency] }))}
                      className="text-[10px] font-bold text-[#0020C2] hover:underline">
                      {showAllSuppliers[currency] ? "Show top 5" : `Show all ${c.top_suppliers.length}`}
                    </button>
                  )}
                </div>
                <div className={`space-y-1 ${showAllSuppliers[currency] ? "max-h-72 overflow-auto pr-1" : ""}`}>
                  {(showAllSuppliers[currency] ? c.top_suppliers : c.top_suppliers.slice(0, 5)).map((s) => {
                    const skey = `supplier:${currency}:${s.supplier_name}`;
                    return (
                    <div key={s.supplier_name}>
                      <button
                        onClick={() => openDrill({
                          key: skey,
                          title: `${s.supplier_name} · ${currency}`,
                          currency, bucket: null, supplier: s.supplier_name,
                        })}
                        className="w-full flex items-center gap-2 text-[11px] text-left rounded px-1 py-0.5
                          hover:bg-[#0020C2]/5"
                        title="Show this supplier's invoices">
                        <div className="min-w-0 flex-1 truncate text-gray-700 underline decoration-dotted underline-offset-2">{s.supplier_name}</div>
                        <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#0020C2]" style={{ width: `${s.percentage_of_total}%` }} />
                        </div>
                        <div className="font-mono text-gray-900 w-28 text-right">{currency} {s.total_outstanding.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                      </button>
                      <DrillPanel forKey={skey} />
                    </div>
                  );})}
                </div>
              </div>
            )}

            {c.insights && c.insights.length > 0 && (
              <div className="space-y-1">
                {c.insights.map((ins, i) => {
                  const color = ins.severity === "critical" ? "text-red-700 bg-red-50 border-red-200"
                    : ins.severity === "warning" ? "text-amber-700 bg-amber-50 border-amber-200"
                    : "text-blue-700 bg-blue-50 border-blue-200";
                  return (
                    <div key={i} className={`text-[11px] px-2 py-1 rounded border ${color}`}>
                      {ins.message}
                    </div>
                  );
                })}
              </div>
            )}

            {c.recommendations && c.recommendations.length > 0 && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Recommended actions</div>
                <ul className="list-disc pl-4 space-y-0.5 text-[11px] text-gray-700">
                  {c.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        );
      })}

      {widget.notes && (widget.notes.on_hold_count > 0 || widget.notes.excluded_unvalidated_count > 0) && (
        <div className="text-[10px] text-gray-400 space-y-0.5">
          {widget.notes.on_hold_count > 0 && <div>· {widget.notes.on_hold_count} invoice(s) on hold (included, flagged)</div>}
          {widget.notes.excluded_unvalidated_count > 0 && <div>· {widget.notes.excluded_unvalidated_count} unvalidated invoice(s) excluded from totals</div>}
        </div>
      )}
    </div>
  );
}

/* ── Supplier / Business Unit LOV picker (search-as-you-type + scroll) ─────────
   kind="supplier"       -> GET /erp-agents/supplier-ageing/lov/suppliers
   kind="business_unit"  -> GET /erp-agents/supplier-ageing/lov/business-units      */
function SupplierAgeingLov({ kind = "supplier", onPick }) {
  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const listRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const reqIdRef = React.useRef(0);

  const PAGE = 25;
  const path = kind === "business_unit"
    ? "/erp-agents/supplier-ageing/lov/business-units"
    : "/erp-agents/supplier-ageing/lov/suppliers";
  const noun = kind === "business_unit" ? "business unit" : "supplier";

  const fetchPage = React.useCallback(async (search, nextOffset, append) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(path, {
        params: { search, offset: nextOffset, limit: PAGE },
      });
      if (myReq !== reqIdRef.current) return;
      const newItems = data?.items || [];
      setItems((prev) => (append ? [...prev, ...newItems] : newItems));
      setHasMore(!!data?.has_more);
      setOffset(nextOffset + newItems.length);
      if (data?.error) setErr("Couldn't load — check the Oracle profile.");
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      setErr(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [path]);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setOffset(0);
      fetchPage(q.trim(), 0, false);
      if (listRef.current) listRef.current.scrollTop = 0;
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, fetchPage]);

  const onScroll = (e) => {
    const el = e.target;
    if (!loading && hasMore && el.scrollHeight - el.scrollTop - el.clientHeight < 40) {
      fetchPage(q.trim(), offset, true);
    }
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid={`supplier-ageing-lov-${kind}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Browse {noun}s
        </span>
      </div>

      <div className="p-2">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Type to search ${noun}s…`}
          className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
            focus:outline-none focus:border-[#0020C2]"
        />
      </div>

      <div ref={listRef} onScroll={onScroll} className="max-h-64 overflow-y-auto">
        {err && <div className="px-3 py-2 text-xs text-red-600">{err}</div>}
        {!err && items.length === 0 && !loading && (
          <div className="px-3 py-3 text-xs text-gray-400">
            No {noun}s found{q.trim() ? ` for "${q.trim()}"` : ""}.
          </div>
        )}
        {items.map((it, i) => (
          <button
            key={`${it.name}-${i}`}
            onClick={() => onPick?.(it.name)}
            className="w-full text-left px-3 py-2 text-xs hover:bg-[#0020C2]/5
              border-b border-gray-50 last:border-0 flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-gray-800">{it.name}</span>
            {it.number && (
              <span className="text-[10px] text-gray-400 font-mono shrink-0">#{it.number}</span>
            )}
          </button>
        ))}
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-2">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
            Loading…
          </div>
        )}
        {!loading && hasMore && (
          <div className="px-3 py-1.5 text-[10px] text-gray-400 text-center">Scroll for more…</div>
        )}
      </div>
    </div>
  );
}

/* ── Chat bubble ─────────────────────────────────────────────────────────── */
function ChatBubble({ msg, onOptionClick }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {!isUser && (
        <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white bg-[#0020C2]">
          <Sparkle size={11} weight="fill" />
        </div>
      )}

      <div className={`text-sm leading-relaxed
        ${isUser
          ? "max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#0020C2] text-white rounded-tr-sm"
          : "flex-1 min-w-0 text-gray-900 pt-0.5"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser ? <p className="whitespace-pre-wrap">{msg.text}</p> : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {!msg.loading && msg.result?.widget?.type === "ageing_dashboard" && (
          <AgeingDashboard widget={msg.result.widget} />
        )}

        {/* Candidate picker (find_supplier / find_business_unit) */}
        {!msg.loading && Array.isArray(msg.result?.next_field_ui?.options)
          && msg.result.next_field_ui.options.length > 0 && (
          <div className="mt-2 space-y-1.5" data-testid="supplier-ageing-candidates">
            {msg.result.next_field_ui.options.map((opt, i) => {
              const label = typeof opt === "string" ? opt : (opt.label ?? opt.value ?? "");
              const value = typeof opt === "string" ? opt : (opt.value ?? opt.label ?? "");
              return (
                <button
                  key={`${label}-${i}`}
                  onClick={() => onOptionClick?.({ value })}
                  className="w-full text-left px-3.5 py-2 text-sm rounded-full border border-[#0020C2]/25
                    bg-[#0020C2]/5 text-[#0020C2] hover:bg-[#0020C2]/10 hover:border-[#0020C2]/40
                    transition-colors truncate">
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Browse LOV — supplier OR business unit */}
        {!msg.loading && msg.result?.lov?.kind && (
          <SupplierAgeingLov
            kind={msg.result.lov.kind}
            onPick={(name) => onOptionClick?.({
              value: msg.result.lov.kind === "business_unit"
                ? `Age this business unit: ${name}`
                : `Age this supplier: ${name}`,
            })}
          />
        )}

        {/* Downloadable report (PDF / Excel) — fetched THROUGH api so the auth
            token is attached (a plain anchor href has no Authorization header). */}
        {!msg.loading && msg.result?.download?.url && (
          <button
            onClick={async () => {
              try {
                const resp = await api.get(msg.result.download.url, { responseType: "blob" });
                const blobUrl = window.URL.createObjectURL(resp.data);
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = msg.result.download.filename || "report";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
              } catch (e) {
                window.alert(`Download failed: ${e?.response?.status === 404
                  ? "the report expired — please regenerate it."
                  : (e?.message || "unknown error")}`);
              }
            }}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider
              border border-[#0020C2] text-[#0020C2] bg-[#0020C2]/5 px-3 py-1.5 rounded-lg
              hover:bg-[#0020C2] hover:text-white transition-colors">
            <CheckCircle size={12} weight="fill" />
            Download {String(msg.result.download.format || "").toUpperCase()} report
          </button>
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Input bar ───────────────────────────────────────────────────────────── */
function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none
            outline-none placeholder-gray-400 font-mono disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">{"\u21b5"} send {"\u00b7"} shift+{"\u21b5"} new line</span>
    </div>
  );
}

/* ── Chat history control ─────────────────────────────────────────────────── */
function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/erp-agents/${agentKey}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`);
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button
        onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
        data-testid="chat-new"
        title="Start a new conversation">
        <Plus size={13} weight="bold" /> New Chat
      </button>

      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
          data-testid="chat-history-toggle"
          title="Chat history">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg
            max-h-80 overflow-y-auto" data-testid="chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>
            )}
            {sessions.map((s) => (
              <div
                key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold uppercase
                      ${s.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : s.status === "cancelled" ? "bg-gray-200 text-gray-500"
                        : "bg-blue-100 text-blue-700"}`}>
                      {s.status}
                    </span>
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="text-[#DC2626] shrink-0 p-1" title="Delete">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Supplier Ageing modal ──────────────────────────────────────────────
   Chat-only (no wizard panel) — the whole modal is the conversation, like
   InvoiceAgeingModal. Mounted from AgentRunModal via:
     if (openAgent === "supplier-ageing") return <SupplierAgeingModal />;       */
export default function SupplierAgeingModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [sessionId, setSessionId] = React.useState(() => crypto.randomUUID());

  // Period (date-window) pills — sent as days_overdue on every chat POST and
  // applied to the NEXT message. null = "All" (no window).
  const DAYS_OPTIONS = [
    { label: "All", value: null },
    { label: "30",  value: 30 },
    { label: "90",  value: 90 },
    { label: "120", value: 120 },
    { label: "360", value: 360 },
  ];
  const [daysOverdue, setDaysOverdue] = React.useState(null);

  const agent = agents.find((a) => a.key === openAgent);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const ts = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setBusy(false);
    setChatStarted(false);
    setSessionId(crypto.randomUUID());
    setDaysOverdue(null);
  }, [openAgent]);

  const close = () => {
    setOpenAgent(null);
    setMessages([]);
    setChatStarted(false);
  };

  const sendMessage = async (text) => {
    const rawText = (text || "").trim();
    if (!rawText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: rawText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const body = { session_id: sessionId, message: rawText };
      if (daysOverdue != null) body.days_overdue = String(daysOverdue);

      const res = await api.post(CHAT_ENDPOINT, body);
      const data = res.data;
      const status = (data?.status || "").toLowerCase();
      const backendMsg = data?.reply || data?.response || data?.message;

      const reply =
        ["ok", "success", "completed"].includes(status)
          ? backendMsg || "Here's the ageing breakdown."
          : status === "needs_input"
            ? backendMsg || "Pick a supplier or business unit below."
            : status === "oracle_error"
              ? backendMsg || `Oracle returned an error (HTTP ${data?.http_status}).`
              : status === "unconfigured"
                ? backendMsg || "Oracle profile isn't set up. Click the gear to configure it."
                : backendMsg || "Something unexpected happened.";

      const now2 = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading ? { ...m, loading: false, text: reply, result: data, timestamp: now2 } : m
        )
      );
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `Error: ${e?.response?.data?.detail || e.message}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(`/erp-agents/${AGENT_KEY}/sessions/new`);
      if (data?.session_id) newId = data.session_id;
    } catch { /* fall back to local uuid */ }
    setSessionId(newId);
    setMessages([]);
    setInput("");
    setChatStarted(false);
    setDaysOverdue(null);
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/${AGENT_KEY}/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i,
        role: m.role,
        text: m.content,
        timestamp: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : ts(),
        result: m.widget ? { widget: m.widget }
          : m.next_field_ui ? { next_field_ui: m.next_field_ui }
            : m.lov ? { lov: m.lov } : undefined,
      }));
      setSessionId(sid);
      setMessages(loaded);
      setChatStarted(true);
    } catch (e) {
      window.alert(`Could not open conversation: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (openAgent !== AGENT_KEY) return null;

  /* Period segmented pills — sits at the top under the header. */
  const DaysPills = () => (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
        Period
      </span>
      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
        {DAYS_OPTIONS.map((opt) => {
          const active = daysOverdue === opt.value;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => setDaysOverdue(opt.value)}
              title={opt.value == null
                ? "All invoices — no date window"
                : `Invoices from the last ${opt.value} days (relative to the most recent invoice)`}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-colors
                ${active ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {opt.label}{opt.value != null ? "d" : ""}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes chatFadeIn { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        .sa-modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .sa-modal-chat {
          width:min(74vw,1120px); height:78vh;
          resize: both; overflow: hidden;
          min-width: 640px;  min-height: 460px;
          max-width: 96vw;   max-height: 94vh;
        }
        .sa-modal-chat::-webkit-resizer { display: none; }
        .sa-modal-resize-hint {
          position: absolute; right: 3px; bottom: 3px; width: 14px; height: 14px;
          cursor: nwse-resize; pointer-events: none; opacity: 0.5;
        }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={() => { if (!chatStarted) close(); }}>
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`relative bg-white text-gray-900 border border-gray-300 flex flex-col
            ${chatStarted ? "sa-modal-chat" : "sa-modal-pre overflow-hidden"}`}
          style={{ transition: chatStarted ? "none" : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {chatStarted && (
            <svg className="sa-modal-resize-hint" viewBox="0 0 14 14" fill="none">
              <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke="#9CA3AF" strokeWidth="1.2" />
            </svg>
          )}

          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP {"\u00b7"} Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label || "Supplier Ageing"}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint || "Age your open AP invoices by supplier or business unit."}</p>

              <div className="mt-2">
                <DaysPills />
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <ChatHistoryControl
                    agentKey={AGENT_KEY}
                    currentSessionId={sessionId}
                    onNewChat={startNewChat}
                    onOpenSession={openSession}
                  />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen(true); }}
                data-testid="agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body — single-column chat (no wizard) */}
          {!chatStarted ? (
            <>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 space-y-2">
                  <div className="flex items-center gap-1.5 text-[#0020C2] font-bold text-xs uppercase tracking-wider">
                    <Sparkle size={12} weight="fill" /> How this works
                  </div>
                  <p>Ask me to age a supplier or a business unit's open AP invoices. For example:</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    <li>"Age supplier Acme Corp"</li>
                    <li>"Age this business unit"</li>
                    <li>"Show all suppliers" or "Show all business units"</li>
                    <li>"Compare Vision Operations and Vision Foods"</li>
                  </ul>
                  <p className="text-xs text-gray-400">
                    Use the <span className="font-semibold">Period</span> pills above to limit to invoices from
                    the last N days. Everything is bucketed (Current, 1-30, 31-60, 61-90, 90+) per currency.
                  </p>
                </div>
              </div>
              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder={agent?.hint || "Ask to age a supplier or business unit…"}
              />
            </>
          ) : (
            <div className="flex flex-col flex-1 overflow-hidden"
              style={{ animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                {messages.map((msg) => (
                  <ChatBubble
                    key={msg.id}
                    msg={msg}
                    onOptionClick={(opt) => sendMessage(opt.value)}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder="Continue the conversation…"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}